import React from "react";
import LegalLayout from "../components/legal/LegalLayout";
import SEOHead from "../components/SEOHead";
import { ScrollText } from "lucide-react";

const SECTIONS = [
  {
    id: "the-basics",
    label: "1. The basics",
    content: (
      <>
        <p>
          Hello. These are the terms for using Binshare. If you use the
          Binshare app, website or any related service (which we call the{" "}
          <strong>Service</strong>), these terms apply to you. Please read
          them — we&apos;ve kept them in plain English.
        </p>
        <p>
          When we say <strong>&quot;Binshare&quot;</strong>, <strong>&quot;we&quot;</strong> or{" "}
          <strong>&quot;us&quot;</strong> we mean{" "}
          <strong>Binshare Ltd</strong>, a company registered in England &amp;
          Wales (Company No. 16909547), with its registered office at
          Caernarfon, Gwynedd, United Kingdom.
        </p>
        <p>
          By creating an account or using the Service you agree to these terms
          and to our{" "}
          <a
            href="/privacy"
            className="text-sage-700 underline underline-offset-2 hover:text-sage-800"
          >
            Privacy Policy
          </a>{" "}
          and{" "}
          <a
            href="/acceptable-use"
            className="text-sage-700 underline underline-offset-2 hover:text-sage-800"
          >
            Acceptable Use Policy
          </a>
          . If you don&apos;t agree, please don&apos;t use the Service.
        </p>
      </>
    ),
  },
  {
    id: "who-can-use",
    label: "2. Who can use Binshare",
    content: (
      <>
        <p>To use Binshare you must:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>be at least 18 years old;</li>
          <li>
            live in the UK, or run a UK-registered business;
          </li>
          <li>be able to enter into a legally binding contract; and</li>
          <li>not be banned from using the Service by law.</li>
        </ul>
        <p>
          By using the Service you promise the above is true. If any of it
          isn&apos;t, please stop using the Service.
        </p>
      </>
    ),
  },
  {
    id: "your-account",
    label: "3. Your account",
    content: (
      <p>
        You&apos;re responsible for keeping your login safe and for everything
        that happens on your account. Please give us accurate details when you
        sign up and keep them up to date. If you think someone else has
        accessed your account, email us at{" "}
        <a
          href="mailto:support@binshare.co.uk"
          className="text-sage-700 underline underline-offset-2"
        >
          support@binshare.co.uk
        </a>{" "}
        immediately.
      </p>
    ),
  },
  {
    id: "what-binshare-is",
    label: "4. What Binshare is (and isn't)",
    content: (
      <>
        <p>
          <strong>Binshare is a marketplace platform.</strong> We connect
          customers with independent, registered waste carriers. Carriers are
          checked against the official waste carrier register for their area
          before they can operate on the platform.
        </p>
        <p>
          <strong>
            We are not a waste carrier. We do not collect, transport or dispose
            of waste ourselves.
          </strong>{" "}
          Carriers are independent businesses — not employees or agents of
          Binshare.
        </p>
      </>
    ),
  },
  {
    id: "contract-formation",
    label: "5. How the contract works",
    content: (
      <>
        <p>
          When you post a job on Binshare, carriers can send you quotes.{" "}
          <strong>
            A contract is formed directly between you (the customer) and the
            carrier the moment you accept a quote and confirm payment.
          </strong>{" "}
          That contract is between the two of you — Binshare is not a party to
          it.
        </p>
        <p>
          We provide the platform, check carriers against the official waste
          carrier register for their area, process the payment, and generate
          a digital receipt for every job. Everything about the
          waste job itself — timing, access, condition of items, disposal
          route — is agreed and delivered between the customer and the
          carrier.
        </p>
        <p>
          If something goes wrong, we&apos;ll always try to help mediate — but
          your legal relationship for the collection sits with the carrier.
        </p>
      </>
    ),
  },
  {
    id: "your-promises",
    label: "6. Your promises to us",
    content: (
      <>
        <p>When you use Binshare, you confirm and agree that:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>
            you are at least <strong>18 years old</strong>;
          </li>
          <li>
            you are <strong>legally entitled</strong> to arrange the collection
            or removal of the waste you post — either as the owner, occupier,
            or with the express permission of the owner;
          </li>
          <li>
            the waste is <strong>accurately described</strong> in your listing,
            including type, volume, weight and location;
          </li>
          <li>
            the waste{" "}
            <strong>
              does not contain any undisclosed hazardous materials
            </strong>{" "}
            — including asbestos, clinical waste, fuels, oils, chemicals,
            gas cylinders, batteries outside council schemes, or any special /
            hazardous waste as defined under UK law;
          </li>
          <li>
            you will pay the agreed price via the Service and not try to
            circumvent the platform by paying the carrier directly off-app.
          </li>
        </ul>
        <p>
          If any of these promises turn out to be untrue, you may be liable to
          us or to the carrier for any resulting loss.
        </p>
      </>
    ),
  },
  {
    id: "carrier-obligations",
    label: "7. Carrier obligations",
    content: (
      <>
        <p>If you register as a waste carrier you also confirm that:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>
            you hold a <strong>current waste carrier registration</strong>{" "}
            with the official register for your area (Natural Resources Wales,
            the Environment Agency, SEPA or NIEA) and will keep it valid;
          </li>
          <li>
            you hold appropriate{" "}
            <strong>public liability and motor insurance</strong> for the
            collections you accept;
          </li>
          <li>
            you will only transfer collected waste to{" "}
            <strong>permitted waste-management sites</strong>;
          </li>
          <li>
            you will attend booked jobs on time, or promptly notify the
            customer if you can&apos;t; and
          </li>
          <li>
            you will not solicit Binshare customers off-platform for 12 months
            after being introduced to them through the Service.
          </li>
        </ul>
        <p>
          We may check your credentials at any time and suspend your account
          if we can&apos;t confirm a valid waste carrier registration.
        </p>
      </>
    ),
  },
  {
    id: "fees-payments",
    label: "8. Fees and payments",
    content: (
      <>
        <p>
          Payments for jobs are processed in-app through our payment provider.
          Binshare charges a flat <strong>5% service fee</strong> on each
          completed job, shown clearly before you confirm the booking. No
          listing fees, no subscriptions.
        </p>
        <p>
          Carrier payouts land in your linked UK bank account, usually within{" "}
          <strong>five (5) working days</strong> of the job being completed.
          Prices are in pounds sterling and include any applicable UK VAT
          unless otherwise stated.
        </p>
      </>
    ),
  },
  {
    id: "protection",
    label: "9. £500 Flytipping Guarantee",
    content: (
      <>
        <p>
          Every completed job on Binshare comes with our{" "}
          <strong>£500 Flytipping Guarantee</strong>. If the carrier fails to
          collect the waste as agreed, or damages your property while doing so,
          we&apos;ll cover reasonable costs up to £500 per completed job — at
          our discretion, and provided you report the issue within 7 days of
          the job.
        </p>
        <p>
          The £500 Flytipping Guarantee does not cover: hazardous waste incidents
          the customer didn&apos;t disclose; issues arising from access
          conditions the customer didn&apos;t describe accurately; or claims
          for indirect or consequential loss.
        </p>
      </>
    ),
  },
  {
    id: "cancellations",
    label: "10. Cancellations and refunds",
    content: (
      <>
        <p>
          You can cancel a booked job for free up to <strong>24 hours</strong>{" "}
          before the collection time. Cancellations inside 24 hours may attract
          a charge of up to 50% of the agreed price, payable to the carrier.
        </p>
        <p>
          If a carrier doesn&apos;t attend, or does the job to a materially
          inadequate standard, we&apos;ll mediate in good faith and may issue
          a full or partial refund at our discretion. Your statutory rights
          under the Consumer Rights Act 2015 and Consumer Contracts
          Regulations 2013 are not affected.
        </p>
      </>
    ),
  },
  {
    id: "warranty-disclaimer",
    label: "11. Warranties and limits",
    content: (
      <>
        <p>
          <strong>Binshare is a marketplace platform, not a waste carrier.</strong>{" "}
          The Service is provided <strong>&quot;as is&quot;</strong> and{" "}
          <strong>&quot;as available&quot;</strong>. We do our best to keep everything
          running, but we don&apos;t guarantee it&apos;ll be uninterrupted,
          error-free, or completely secure.
        </p>
        <p>
          Because carriers are independent businesses,{" "}
          <strong>
            we do not warrant the quality, timing, or outcome of any specific
            collection beyond the verification checks and processes described
            in these terms.
          </strong>{" "}
          Any implied warranties are excluded to the fullest extent permitted
          by law.
        </p>
        <p>
          <strong>Our total liability</strong> in any 12-month period arising
          out of the Service is limited to the greater of (a) the amounts you
          paid us in that period, or (b) £100. We&apos;re not liable for
          indirect losses, lost profits, lost goodwill or lost data.
        </p>
        <p>
          Nothing in these terms excludes or limits liability for death or
          personal injury caused by our negligence, for fraud, or for anything
          else that can&apos;t be excluded under UK law.
        </p>
      </>
    ),
  },
  {
    id: "app-stores",
    label: "12. App Store and Google Play",
    content: (
      <>
        <p>
          If you got the Binshare app from Apple&apos;s App Store or Google
          Play, you acknowledge that:
        </p>
        <ul className="list-disc pl-6 space-y-2">
          <li>
            these terms are between you and{" "}
            <strong>Binshare</strong>, not Apple or Google;
          </li>
          <li>
            Apple and Google are not parties to these terms and have no
            obligation to provide the app or support it;
          </li>
          <li>
            you agree to comply with each store&apos;s applicable usage rules
            (e.g. the Apple Media Services Terms, or the Google Play Terms of
            Service);
          </li>
          <li>
            in the event of any conflict between these terms and a store&apos;s
            terms, the store&apos;s terms apply to your download and licence to
            use the app;
          </li>
          <li>
            Apple and Google are third-party beneficiaries of these terms and
            may enforce them against you as they relate to your use of the app.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: "content-reviews",
    label: "13. Reviews and content",
    content: (
      <p>
        You can review carriers and jobs you&apos;ve used. Reviews must be
        honest and based on your own experience — no defamation, no
        discrimination, no impersonation. We may remove content that breaches
        these terms. You keep ownership of what you post, but you give us a
        worldwide, royalty-free licence to display it on the Service.
      </p>
    ),
  },
  {
    id: "prohibited-use",
    label: "14. Things you must not do",
    content: (
      <>
        <p>Don&apos;t use Binshare to:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>fly-tip or otherwise dispose of waste illegally;</li>
          <li>pretend to be someone else or fake a waste carrier registration;</li>
          <li>scrape our data or hack the Service;</li>
          <li>harass, discriminate against or harm other users.</li>
        </ul>
        <p>
          We report suspected illegal waste activity to the relevant UK
          regulator and, where needed, to the police.
        </p>
      </>
    ),
  },
  {
    id: "ip",
    label: "15. Our intellectual property",
    content: (
      <p>
        Binshare and our licensors own the Service — the name, logo, branding
        and software. You get a limited licence to use the Service; nothing
        more. Please don&apos;t copy, reverse engineer or create knock-off
        versions.
      </p>
    ),
  },
  {
    id: "indemnity",
    label: "16. Your indemnity",
    content: (
      <p>
        You agree to cover our reasonable costs (including legal fees) if we
        get sued because you broke these terms or the law — for example, if
        you disposed of waste unlawfully or posted content you shouldn&apos;t
        have.
      </p>
    ),
  },
  {
    id: "termination",
    label: "17. Closing accounts",
    content: (
      <p>
        You can close your account at any time from the app or by emailing us.
        We can suspend or close an account immediately if you break these
        terms, if we&apos;re required to by law, or if your activity puts us
        or other users at risk. Sections that make sense to survive
        termination (like liability, indemnity and governing law) will keep
        applying.
      </p>
    ),
  },
  {
    id: "changes",
    label: "18. Changes to these terms",
    content: (
      <p>
        We may update these terms from time to time as the Service or the law
        changes. The &quot;Effective&quot; date above shows when the current
        version began. We&apos;ll notify you of material changes at least 14
        days in advance. Continued use after that date means you accept the
        new terms.
      </p>
    ),
  },
  {
    id: "general",
    label: "19. General",
    content: (
      <>
        <p>
          <strong>Severability.</strong> If a court decides any part of these
          terms is unenforceable, the rest still applies.
        </p>
        <p>
          <strong>Entire agreement.</strong> These terms (plus the Privacy
          Policy and Acceptable Use Policy) are the entire agreement between
          you and Binshare about the Service. They replace any earlier
          promises or discussions.
        </p>
        <p>
          <strong>Assignment.</strong> You can&apos;t transfer your rights
          under these terms to anyone else without our written consent. We may
          transfer ours to a successor business (for example after a
          restructure or sale).
        </p>
        <p>
          <strong>No waiver.</strong> If we don&apos;t enforce a right under
          these terms straight away, that doesn&apos;t mean we give up that
          right.
        </p>
      </>
    ),
  },
  {
    id: "law",
    label: "20. Governing law",
    content: (
      <p>
        These terms are governed by the laws of{" "}
        <strong>England &amp; Wales</strong>. The courts of England &amp;
        Wales have exclusive jurisdiction, except that consumers resident in
        Scotland or Northern Ireland may bring proceedings locally. Please
        email us first at{" "}
        <a
          href="mailto:support@binshare.co.uk"
          className="text-sage-700 underline underline-offset-2"
        >
          support@binshare.co.uk
        </a>{" "}
        before starting any court claim so we can try to sort it informally.
      </p>
    ),
  },
  {
    id: "contact",
    label: "21. Contact us",
    content: (
      <div className="rounded-2xl border border-moss-200/50 bg-cream-100/60 p-5 sm:p-6 not-prose">
        <p className="font-semibold text-moss-900">Binshare Ltd</p>
        <p className="text-sm text-moss-700 mt-1">
          Company No. 16909547 — Registered in England &amp; Wales
        </p>
        <p className="text-sm text-moss-700 mt-1">
          Registered office: Caernarfon, Gwynedd, United Kingdom
        </p>
        <p className="text-sm mt-3">
          <a
            href="mailto:support@binshare.co.uk"
            className="text-sage-700 underline underline-offset-2 hover:text-sage-800 font-medium"
            data-testid="terms-contact-email"
          >
            support@binshare.co.uk
          </a>
        </p>
      </div>
    ),
  },
];

export default function TermsPage() {
  return (
    <>
      <SEOHead
        path="/terms"
        title="Terms of Service | Binshare Waste Disposal Marketplace UK"
        description="Plain-English terms for using Binshare, the UK waste marketplace connecting customers with registered waste carriers. Read before booking."
        keywords="Binshare terms of service, waste marketplace UK terms, registered waste carriers, duty of care legal"
      />
      <LegalLayout
        testidPrefix="terms"
        eyebrow={
          <>
            <ScrollText className="w-3.5 h-3.5" /> Legal
          </>
        }
        title="Terms of Service"
        intro="Plain-English terms for using Binshare — the UK's waste marketplace. Please read before you use the Service."
        effective="1 February 2026"
        lastUpdated="13 October 2026"
        sections={SECTIONS}
      />
    </>
  );
}
